import type { AvailableCurrencies, BillingAccount } from '@ctct/domain-layer';
import { envs } from '@ctct/infrastructure-layer';
import type { CurrencyPageStore } from '@ctct/state-client-layer';
import type { UpdateCurrencyMutation } from '@ctct/state-server-layer';

import type { TEventSource } from '../../../helpers';
import { currencyEventBus } from '../CurrencyEventBus';
import type { CurrencyUrlAdapter, Flags } from '../types';

export class CurrencyService {
  constructor(
    private pageStore: CurrencyPageStore,
    private availableCurrencies: AvailableCurrencies,
    private billingAccount: BillingAccount,
    private updateCurrencyMutation: UpdateCurrencyMutation,
    private urlAdapter: CurrencyUrlAdapter,
    private flags: Flags
  ) {}

  public api() {
    return {
      getCurrency: () => this.billingAccount.getCurrency(),
      disableSelect: () => this.pageStore.setState({ currencySelectDisabled: true }),
      enableSelect: () => this.pageStore.setState({ currencySelectDisabled: false }),
    };
  }

  public data() {
    return {
      getCurrency: () => this.billingAccount.getCurrency(),

      getCurrencyOptions: () => this.availableCurrencies.getCurrencyOptions(),
    };
  }

  public handlers() {
    return {
      onCurrencyChange: (newCurrency: string) => this.handleCurrencyChange(newCurrency, 'user'),
    };
  }

  public onMount() {
    const [skipInitializationFromUrl, isCoreMutationLockedAccount, isCoreMutationLockPending] = this.flags;
    const { currency: currencyFromUrl, deleteCurrency } = this.urlAdapter.getCurrencyParams();

    if (
      currencyFromUrl &&
      this.availableCurrencies.getAvailableCurrencies().includes(currencyFromUrl.toUpperCase()) &&
      !skipInitializationFromUrl &&
      !isCoreMutationLockedAccount
    ) {
      this.handleCurrencyChange(currencyFromUrl.toUpperCase(), 'onMount');
    }

    if (!isCoreMutationLockPending && envs.isSecure()) deleteCurrency();
  }

  private async handleCurrencyChange(newCurrency: string, source: TEventSource) {
    const [, isCoreMutationLockedAccount] = this.flags;

    if (isCoreMutationLockedAccount && source !== 'asyncResponse') {
      return;
    }

    const existingCurrency = this.billingAccount.getCurrency();

    if (existingCurrency === newCurrency) {
      return;
    }

    try {
      this.pageStore.setState({ currencySelectDisabled: true });
      currencyEventBus.emit('currencyIsChanging', true, { source });

      await this.updateCurrencyMutation.mutateAsync(newCurrency);

      currencyEventBus.emit('currencyHasChanged', newCurrency, { source });
    } catch {
      // should we show error in UI?
    } finally {
      this.pageStore.setState({ currencySelectDisabled: false });
      currencyEventBus.emit('currencyIsChanging', false, { source });
    }
  }
}
